import { useChat } from "@ai-sdk/react";
import {
  PromptInput,
  PromptInputButton,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from "@franklin/ui";
import { ImageIcon, Mic, Paperclip } from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFranklinContext } from "../../context/franklin-context";
import { LoadingMessage, Message } from "./message";
import { WelcomeScreen } from "./welcome-screen";

export function AIChat() {
  const { apiEndpoint, orgId, sdkKey, context } = useFranklinContext();
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { messages, input, handleInputChange, handleSubmit, isLoading } =
    useChat({
      api: apiEndpoint,
      headers: {
        "X-Franklin-Org-Id": orgId,
        "X-Franklin-SDK-Key": sdkKey,
      },
      body: {
        context,
      },
      initialMessages: [],
    });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const onQuickAction = (action: string) => {
    const changeEvent = {
      currentTarget: { value: action } as HTMLTextAreaElement,
      preventDefault: () => {
        /* noop */
      },
      stopPropagation: () => {
        /* noop */
      },
      target: { value: action } as HTMLTextAreaElement,
    } as ChangeEvent<HTMLTextAreaElement>;
    setInputValue(action);
    handleInputChange(changeEvent);

    setTimeout(() => {
      const submitEvent = {
        preventDefault: () => {
          /* noop */
        },
        stopPropagation: () => {
          /* noop */
        },
      } as FormEvent<HTMLFormElement>;
      handleSubmit(submitEvent);
      setInputValue("");
    }, 100);
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inputValue.trim() || input?.trim()) {
      handleSubmit(event);
      setInputValue("");
    }
  };

  const isSubmitDisabled = !(inputValue.trim() || input?.trim());

  return (
    <div className="flex h-full flex-col bg-[var(--franklin-surface)]">
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <WelcomeScreen
            inputValue={inputValue || input || ""}
            isLoading={isLoading}
            onInputChange={(e) => {
              setInputValue(e.target.value);
              handleInputChange(e);
            }}
            onQuickAction={onQuickAction}
            onSubmit={handleFormSubmit}
          />
        ) : (
          <div className="flex flex-col gap-4 px-6 pt-6 pb-4">
            {messages.map((message) => (
              <Message
                content={message.content}
                key={message.id}
                role={message.role}
              />
            ))}
            {isLoading && <LoadingMessage />}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {messages.length > 0 && (
        <div className="px-4 pb-4">
          <PromptInput
            onSubmit={(message, event) => {
              event.preventDefault();
              if (message.text?.trim()) {
                handleFormSubmit(event);
              }
            }}
          >
            <PromptInputTextarea
              onChange={(event) => {
                setInputValue(event.target.value);
                handleInputChange(event);
              }}
              placeholder="Message Franklin..."
              value={inputValue || input || ""}
            />
            <PromptInputFooter>
              <PromptInputTools>
                <PromptInputButton title="Attach file">
                  <Paperclip className="size-4" />
                </PromptInputButton>
                <PromptInputButton title="Add image">
                  <ImageIcon className="size-4" />
                </PromptInputButton>
                <PromptInputButton title="Voice input">
                  <Mic className="size-4" />
                </PromptInputButton>
              </PromptInputTools>
              <PromptInputSubmit
                disabled={isSubmitDisabled}
                status={isLoading ? "streaming" : "ready"}
              />
            </PromptInputFooter>
          </PromptInput>
        </div>
      )}
    </div>
  );
}
